import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { OutcomeLogicTesterService } from '../outcome-logic-tester.service';
import { AndOrOperator, Outcome, OutcomeAnswer, OutcomeOperator } from '../../../widgets.model';

@Component({
  selector: 'app-outcome-test-logic',
  templateUrl: './outcome-test-logic.component.html',
  styleUrls: ['./outcome-test-logic.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OutcomeTestLogicComponent implements OnInit, OnChanges {
  @Input() outcomes: Outcome[] = [];
  @Input() inputComponents: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
    isMultiSelect: boolean;
  }[] = [];
  @Input() scoreInput: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
    isMultiSelect: boolean;
  } | null = null;
  @Input() minScore: number = 0;
  @Input() maxScore: number = 0;
  @Input() hasScore: boolean = false;
  @Input() updateTrigger: number = 0;

  @Output() matchedOutcomeIndexChange = new EventEmitter<number | null>();
  @Output() close = new EventEmitter<void>();

  testInputs: Record<string, any> = {};
  matchedOutcomeIndex: number | null = null;
  continuouslyCheckLogic: boolean = true;
  showOnlyUsedInputs: boolean = true;
  regularInputs: any[] = [];

  constructor(private readonly outcomeLogicTesterService: OutcomeLogicTesterService) {}

  ngOnInit(): void {
    this.initializeInputs();
    this.onTestInputChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputComponents']) {
      this.regularInputs = this.inputComponents.filter((c) => c.name.toLowerCase() !== 'score');
      this.initializeInputs();
      this.onTestInputChange();
    }

    if (changes['outcomes'] || changes['updateTrigger']) {
      this.testLogic();
    }
  }

  private initializeInputs() {
    this.inputComponents.forEach((c) => {
      if (!this.testInputs[c.name]) {
        if (c.values && c.values.length > 0) {
          if (c.isMultiSelect) {
            this.testInputs[c.name] = [];
          } else {
            this.testInputs[c.name] = '';
          }
        } else {
          this.testInputs[c.name] = '';
        }
      }
    });
  }

  get filteredRegularInputs() {
    if (!this.showOnlyUsedInputs) {
      return this.regularInputs;
    }
    const usedInputNames = new Set<string>();
    this.outcomes.forEach((outcome) => {
      outcome.answers.forEach((answer) => {
        usedInputNames.add(answer.question);
      });
    });

    const isScoreUsed =
      !!this.scoreInput &&
      this.outcomes.some((outcome) => outcome.answers.some((answer) => answer.question === this.scoreInput!.name));

    return this.regularInputs.filter(
      (input) =>
        usedInputNames.has(input.name) ||
        (isScoreUsed && input.valuesWithScore?.some((v: { score: number }) => v.score !== 0)),
    );
  }

  get showScoreInput(): boolean {
    if (!this.hasScore) {
      return true;
    }
    if (!this.scoreInput) {
      return false;
    }
    if (!this.showOnlyUsedInputs) {
      return true;
    }
    return this.outcomes.some((outcome) => outcome.answers.some((answer) => answer.question === this.scoreInput!.name));
  }

  onTestInputChange() {
    if (this.scoreInput) {
      let totalScore = 0;
      for (const key in this.testInputs) {
        if (key === this.scoreInput.name) {
          continue;
        }

        const component = this.inputComponents.find((c) => c.name === key);
        if (component && component.valuesWithScore) {
          const selectedValues = this.testInputs[key];
          if (Array.isArray(selectedValues)) {
            selectedValues.forEach((val) => {
              const scoreItem = component.valuesWithScore.find((v) => v.value === val);
              if (scoreItem) {
                totalScore += scoreItem.score;
              }
            });
          } else {
            const scoreItem = component.valuesWithScore.find((v) => v.value === selectedValues);
            if (scoreItem) {
              totalScore += scoreItem.score;
            }
          }
        }
      }
      this.testInputs[this.scoreInput.name] = totalScore;
    }

    if (this.continuouslyCheckLogic) {
      this.testLogic();
    }
  }

  testLogic() {
    const matchedOutcome = this.outcomeLogicTesterService.evaluate(this.outcomes, this.testInputs);
    if (matchedOutcome) {
      this.matchedOutcomeIndex = this.outcomes.indexOf(matchedOutcome);
    } else {
      this.matchedOutcomeIndex = null;
    }
    this.matchedOutcomeIndexChange.emit(this.matchedOutcomeIndex);
  }

  onClose() {
    this.close.emit();
  }

  getSelectedItems(component: any): { value: string; score: number; isCorrect: boolean }[] {
    const selectedValue = this.testInputs[component.name];
    if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
      return [];
    }

    if (Array.isArray(selectedValue)) {
      return component.valuesWithScore.filter((v: any) => selectedValue.includes(v.value));
    } else {
      const item = component.valuesWithScore.find((v: any) => v.value === selectedValue);
      return item ? [item] : [];
    }
  }
}
